import { useParams, Link } from "react-router-dom";
import { useEffect, useState, useMemo, useCallback } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ArrowLeft, Clock, Sun, Moon, Tag, List, X, Copy, Check } from "lucide-react";
import { articles } from "@/data/articles";
import { Button } from "@/components/ui/button";
import sentinelIcon from "@/assets/sentinelhub-icon.svg";
import { useLanguage } from "@/i18n/LanguageContext";
import { translations, t, Language } from "@/i18n/translations";
import { Helmet } from "react-helmet-async";
import { useTheme } from "@/hooks/useTheme";
import { LANG_HTML, LOCALE, SITE_URL, localizedPath } from "@/lib/routes";

const LANGUAGES: { code: Language; label: string; flag: string; altText: string }[] = [
  { code: "pt", label: "PT", flag: "https://flagcdn.com/w40/br.png", altText: "Selecione o idioma Português" },
  { code: "es", label: "ES", flag: "https://flagcdn.com/w40/es.png", altText: "Selecciona el idioma Español" },
  { code: "en", label: "EN", flag: "https://flagcdn.com/w40/us.png", altText: "Select English language" },
];

const CodeBlock = ({ children, isDark, ...props }: any) => {
  const [copied, setCopied] = useState(false);
  const codeText = useMemo(() => {
    const extractText = (node: any): string => {
      if (typeof node === "string") return node;
      if (Array.isArray(node)) return node.map(extractText).join("");
      if (node?.props?.children) return extractText(node.props.children);
      return "";
    };
    return extractText(children);
  }, [children]);
  return (
    <div className="relative group">
      <pre {...props}>{children}</pre>
      <button
        onClick={() => {
          navigator.clipboard.writeText(codeText);
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        }}
        className={`absolute top-2 right-2 p-1.5 rounded-md opacity-0 group-hover:opacity-100 transition-opacity ${
          isDark
            ? "bg-[hsl(222,47%,15%)] hover:bg-[hsl(222,47%,20%)] text-[hsl(215,20%,65%)]"
            : "bg-gray-200 hover:bg-gray-300 text-gray-600"
        }`}
        title="Copiar código"
        aria-label={copied ? "Código copiado" : "Copiar código"}
      >
        {copied ? <Check className="h-3.5 w-3.5 text-green-500" /> : <Copy className="h-3.5 w-3.5" />}
      </button>
    </div>
  );
};

interface TocItem {
  id: string;
  text: string;
  level: number;
}

function extractToc(md: string): TocItem[] {
  const lines = md.split("\n");
  const items: TocItem[] = [];
  let inCode = false;
  for (const line of lines) {
    if (line.trim().startsWith("```")) { inCode = !inCode; continue; }
    if (inCode) continue;
    const match = line.match(/^(#{2})\s+(.+)/);
    if (match) {
      const text = match[2].replace(/[*_`]/g, "").trim();
      const id = text
        .toLowerCase()
        .replace(/[^\w\sà-ú-]/g, "")
        .replace(/\s+/g, "-");
      items.push({ id, text, level: match[1].length });
    }
  }
  return items;
}

function slugify(text: string) {
  return text
    .toLowerCase()
    .replace(/[^\w\sà-ú-]/g, "")
    .replace(/\s+/g, "-");
}

const InsightArticle = () => {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);
  const { lang, setLang } = useLanguage();
  const ins = translations.insights;
  const [markdown, setMarkdown] = useState("");
  const { theme, toggleTheme } = useTheme();
  const isDark = theme === "dark";
  const setIsDark = (_: boolean) => toggleTheme();
  const [activeId, setActiveId] = useState("");
  const [tocOpen, setTocOpen] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);

  const toc = useMemo(() => extractToc(markdown), [markdown]);

  // Strip h1, italic excerpt and first hr from markdown to avoid duplication
  const strippedMarkdown = useMemo(() => {
    if (!markdown) return "";
    const lines = markdown.split("\n");
    let start = 0;
    // Skip H1 line
    if (lines[start]?.startsWith("# ")) start++;
    // Skip empty lines
    while (start < lines.length && lines[start].trim() === "") start++;
    // Skip italic excerpt line(s)
    if (lines[start]?.startsWith("*") && lines[start]?.endsWith("*")) start++;
    // Skip empty lines
    while (start < lines.length && lines[start].trim() === "") start++;
    // Skip hr (---)
    if (lines[start]?.trim().match(/^-{3,}$/)) start++;
    return lines.slice(start).join("\n");
  }, [markdown]);

  useEffect(() => {
    if (!article) return;
    fetch(article.markdownPath[lang])
      .then((r) => r.text())
      .then(setMarkdown)
      .catch(() => setMarkdown("# Erro ao carregar o artigo"));
  }, [article, lang]);

  // Theme is now managed globally by useTheme hook

  // Intersection observer for active heading
  useEffect(() => {
    if (toc.length === 0) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting);
        if (visible.length > 0) {
          setActiveId(visible[0].target.id);
        }
      },
      { rootMargin: "-80px 0px -70% 0px", threshold: 0 }
    );
    toc.forEach((item) => {
      const el = document.getElementById(item.id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [toc]);

  const scrollTo = useCallback((id: string) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      setTocOpen(false);
    }
  }, []);

  const insightsPath = localizedPath("insights", lang);
  const articleCanonical = article ? `${SITE_URL}${localizedPath("insightsArticle", lang, article.slug)}` : "";

  if (!article) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">{t(ins.notFound, lang)}</h1>
          <Link to={insightsPath}>
            <Button>{t(ins.back, lang)}</Button>
          </Link>
        </div>
      </div>
    );
  }

  const tocContent = (
    <nav>
      <p className={`text-[11px] font-semibold uppercase tracking-wider mb-2 ${isDark ? "text-muted-foreground" : "text-muted-foreground"}`}>
        {t(ins.toc, lang)}
      </p>
      <div className="space-y-px">
        {toc.map((item) => (
          <button
            key={item.id}
            onClick={() => scrollTo(item.id)}
            className={`block w-full text-left transition-colors rounded px-2 leading-snug ${
              item.level === 3 ? "pl-4 text-xs py-1" : "text-[13px] py-1.5 font-medium"
            } ${
              activeId === item.id
                ? isDark
                  ? "text-[hsl(197,100%,47%)] bg-[hsl(197,100%,47%)]/10"
                  : "text-blue-600 bg-blue-50"
                : isDark
                  ? "text-[hsl(215,20%,60%)] hover:text-[hsl(210,40%,90%)]"
                  : "text-muted-foreground hover:text-foreground"
            }`}
          >
            {item.text}
          </button>
        ))}
      </div>
    </nav>
  );

  return (
    <div
      className={`min-h-screen transition-colors duration-300 ${
        isDark
          ? "bg-[hsl(222,47%,5%)] text-[hsl(210,40%,98%)]"
          : "bg-[hsl(0,0%,98%)] text-[hsl(222,47%,15%)]"
      }`}
    >
      <Helmet>
        <html lang={LANG_HTML[lang]} />
        <title>{`${article.title[lang]} | SentinelHub`}</title>
        <meta name="description" content={article.excerpt[lang]} />
        <link rel="canonical" href={articleCanonical} />
        <link rel="alternate" hrefLang="pt-BR" href={`${SITE_URL}${localizedPath("insightsArticle", "pt", article.slug)}`} />
        <link rel="alternate" hrefLang="en" href={`${SITE_URL}${localizedPath("insightsArticle", "en", article.slug)}`} />
        <link rel="alternate" hrefLang="es" href={`${SITE_URL}${localizedPath("insightsArticle", "es", article.slug)}`} />
        <link rel="alternate" hrefLang="x-default" href={`${SITE_URL}${localizedPath("insightsArticle", "pt", article.slug)}`} />
        <meta property="og:title" content={article.title[lang]} />
        <meta property="og:description" content={article.excerpt[lang]} />
        <meta property="og:url" content={articleCanonical} />
        <meta property="og:type" content="article" />
        <meta property="og:locale" content={LOCALE[lang]} />
        <meta property="og:image" content={`${SITE_URL}${article.ogImage}`} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="article:published_time" content={article.date} />
        <meta property="article:section" content={article.category} />
        {article.tags[lang].map((tag) => (
          <meta key={tag} property="article:tag" content={tag} />
        ))}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={article.title[lang]} />
        <meta name="twitter:description" content={article.excerpt[lang]} />
        <meta name="twitter:image" content={`${SITE_URL}${article.ogImage}`} />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": article.title[lang],
            "description": article.excerpt[lang],
            "datePublished": article.date,
            "dateModified": article.date,
            "author": { "@type": "Organization", "name": "SentinelHub", "url": SITE_URL },
            "publisher": { "@type": "Organization", "name": "SentinelHub", "url": SITE_URL, "logo": { "@type": "ImageObject", "url": `${SITE_URL}/favicon.svg` } },
            "mainEntityOfPage": { "@type": "WebPage", "@id": articleCanonical },
            "image": `${SITE_URL}${article.ogImage}`,
            "keywords": article.tags[lang].join(", "),
            "articleSection": article.category,
            "inLanguage": LANG_HTML[lang],
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "SentinelHub", item: `${SITE_URL}${localizedPath("home", lang)}` },
              { "@type": "ListItem", position: 2, name: "Insights", item: `${SITE_URL}${localizedPath("insights", lang)}` },
              { "@type": "ListItem", position: 3, name: article.title[lang], item: articleCanonical },
            ],
          })}
        </script>
      </Helmet>

      {/* Top bar */}
      <header
        className={`sticky top-0 z-50 border-b backdrop-blur-xl transition-colors ${
          isDark
            ? "bg-[hsl(222,47%,5%)]/80 border-[hsl(222,30%,18%)]"
            : "bg-white/80 border-gray-200"
        }`}
      >
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            {/* Left: back + logo */}
            <div className="flex items-center gap-2 sm:gap-4 min-w-0">
              <Link
                to={insightsPath}
                aria-label={t({ pt: "Voltar para todos os artigos do Insights", es: "Volver a todos los artículos de Insights", en: "Back to all Insights articles" }, lang)}
                className={`flex items-center gap-1 sm:gap-2 text-sm font-medium transition-colors flex-shrink-0 ${
                  isDark ? "text-[hsl(215,20%,65%)] hover:text-[hsl(210,40%,98%)]" : "text-muted-foreground hover:text-foreground"
                }`}
              >
                <ArrowLeft className="h-4 w-4" aria-hidden="true" />
                <span className="hidden sm:inline">{t(ins.back, lang)}</span>
              </Link>
              <div className="flex items-center gap-2 min-w-0">
                <img src={sentinelIcon} alt="SentinelHub Security Platform" className="h-6 w-6 sm:h-7 sm:w-7 flex-shrink-0" />
                <span className="text-sm font-bold font-display truncate">
                  Sentinel<span className="text-[hsl(197,100%,47%)]">Hub</span>
                  <span className={`hidden sm:inline ml-2 text-xs font-normal ${isDark ? "text-muted-foreground" : "text-muted-foreground"}`}>
                    Insights
                  </span>
                </span>
              </div>
            </div>

            {/* Right: actions */}
            <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
              {/* Language selector - desktop only in row */}
              <div className={`hidden sm:flex items-center gap-0.5 rounded-lg p-0.5 ${isDark ? "bg-[hsl(222,47%,12%)]" : "bg-gray-100"}`}>
                {LANGUAGES.map((l) => (
                  <button
                    key={l.code}
                    onClick={() => setLang(l.code)}
                    className={`flex items-center gap-1 px-2 py-1.5 rounded-md text-xs transition-all ${
                      lang === l.code
                        ? isDark ? "bg-[hsl(197,100%,47%)]/20 text-[hsl(197,100%,47%)]" : "bg-blue-50 text-blue-600"
                        : isDark ? "text-[hsl(215,20%,65%)] hover:text-[hsl(210,40%,98%)]" : "text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    <img src={l.flag} alt={l.altText} className="w-4 h-3 object-cover rounded-sm" />
                    <span className="font-medium">{l.label}</span>
                  </button>
                ))}
              </div>

              {/* Mobile TOC toggle */}
              <button
                onClick={() => setTocOpen(!tocOpen)}
                className={`lg:hidden p-2 rounded-lg transition-colors ${
                  isDark
                    ? "bg-[hsl(222,47%,12%)] hover:bg-[hsl(222,47%,16%)] text-[hsl(210,40%,98%)]"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700"
                }`}
                title={t(ins.toc, lang)}
                aria-label={t(ins.toc, lang)}
                aria-expanded={tocOpen}
              >
                <List className="h-4 w-4" />
              </button>

              <button
                onClick={() => setIsDark(!isDark)}
                className={`p-2 rounded-lg transition-colors ${
                  isDark
                    ? "bg-[hsl(222,47%,12%)] hover:bg-[hsl(222,47%,16%)] text-[hsl(210,40%,98%)]"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700"
                }`}
                title={isDark ? t(ins.lightMode, lang) : t(ins.darkMode, lang)}
                aria-label={isDark ? t(ins.lightMode, lang) : t(ins.darkMode, lang)}
              >
                {isDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
              </button>
            </div>
          </div>

          {/* Mobile language selector - second row */}
          <div className={`flex sm:hidden items-center justify-center gap-0.5 rounded-lg p-0.5 mt-2 ${isDark ? "bg-[hsl(222,47%,12%)]" : "bg-gray-100"}`}>
            {LANGUAGES.map((l) => (
              <button
                key={l.code}
                onClick={() => setLang(l.code)}
                className={`flex items-center gap-1 px-3 py-1.5 rounded-md text-xs transition-all ${
                  lang === l.code
                    ? isDark ? "bg-[hsl(197,100%,47%)]/20 text-[hsl(197,100%,47%)]" : "bg-blue-50 text-blue-600"
                    : isDark ? "text-[hsl(215,20%,65%)] hover:text-[hsl(210,40%,98%)]" : "text-muted-foreground hover:text-foreground"
                }`}
              >
                <img src={l.flag} alt={l.altText} className="w-4 h-3 object-cover rounded-sm" />
                <span className="font-medium">{l.label}</span>
              </button>
            ))}
          </div>
        </div>
      </header>

      {/* Mobile TOC drawer */}
      {tocOpen && (
        <>
          <div className="fixed inset-0 z-[60] bg-black/50 lg:hidden" onClick={() => setTocOpen(false)} />
          <div
            className={`fixed top-0 right-0 z-[70] h-full w-72 overflow-y-auto p-6 pt-16 shadow-2xl transition-colors lg:hidden ${
              isDark ? "bg-[hsl(222,47%,7%)]" : "bg-white"
            }`}
          >
            <button
              onClick={() => setTocOpen(false)}
              className={`absolute top-4 right-4 p-1 rounded ${
                isDark ? "text-muted-foreground hover:text-white" : "text-muted-foreground hover:text-foreground"
              }`}
              aria-label={t(ins.closeToc, lang)}
            >
              <X className="h-5 w-5" />
            </button>
            {tocContent}
          </div>
        </>
      )}

      {/* Content layout: TOC left + Hero/Article right */}
      <div className="mx-auto px-6 lg:px-10 max-w-[1400px] py-12">
        <div className="flex gap-8">
          {/* Desktop TOC sidebar - LEFT */}
          <aside className="hidden lg:block w-56 flex-shrink-0">
            <div className="sticky top-20 max-h-[60vh] overflow-y-auto pr-1">
              {tocContent}
            </div>
          </aside>

          {/* Main content: Hero + Article */}
          <div className="flex-1 min-w-0 max-w-4xl">
            {/* Hero */}
            <div className={`pb-10 mb-10 border-b ${isDark ? "border-[hsl(222,30%,18%)]" : "border-gray-200"}`}>
              <div className="flex items-center gap-3 mb-4">
                <span
                  className={`text-xs font-medium px-2.5 py-1 rounded-full ${
                    isDark ? "bg-[hsl(197,100%,47%)]/10 text-[hsl(197,100%,47%)]" : "bg-blue-50 text-blue-600"
                  }`}
                >
                  {article.category}
                </span>
                <span className={`text-xs flex items-center gap-1 ${isDark ? "text-[hsl(215,20%,65%)]" : "text-muted-foreground"}`}>
                  <Clock className="h-3 w-3" />
                  {article.readTime}
                </span>
                <span className={`text-xs ${isDark ? "text-[hsl(215,20%,65%)]" : "text-muted-foreground"}`}>
                  {new Date(article.date).toLocaleDateString(lang === "en" ? "en-US" : lang === "es" ? "es-ES" : "pt-BR")}
                </span>
              </div>
              <h1 className="text-3xl md:text-4xl font-bold font-display mb-4">
                {article.title[lang]}
              </h1>
              <p className={`text-lg ${isDark ? "text-[hsl(215,20%,65%)]" : "text-gray-600"}`}>
                {article.excerpt[lang]}
              </p>
              <div className="flex flex-wrap items-center gap-2 mt-4">
                {article.tags[lang].map((tag) => (
                  <span
                    key={tag}
                    className={`text-xs px-2 py-0.5 rounded flex items-center gap-1 ${
                      isDark ? "bg-[hsl(222,47%,12%)] text-[hsl(215,20%,65%)]" : "bg-gray-100 text-gray-600"
                    }`}
                  >
                    <Tag className="h-2.5 w-2.5" />
                    {tag}
                  </span>
                ))}

                <span className={`mx-1 h-4 w-px ${isDark ? "bg-[hsl(222,30%,25%)]" : "bg-gray-300"}`} />

                {/* Share buttons */}
                {(() => {
                  const shareUrl = articleCanonical;
                  const shareText = `${article.title[lang]} — ${shareUrl}`;
                  const btnClass = `inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-md transition-colors cursor-pointer ${
                    isDark
                      ? "bg-[hsl(222,47%,12%)] text-[hsl(215,20%,65%)] hover:bg-[hsl(222,47%,18%)] hover:text-[hsl(210,40%,98%)]"
                      : "bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-900"
                  }`;

                  const isMobile = typeof navigator !== "undefined" && /Mobi|Android/i.test(navigator.userAgent);


                  const handleCopyLink = () => {
                    navigator.clipboard.writeText(shareUrl).then(() => {
                      setLinkCopied(true);
                      setTimeout(() => setLinkCopied(false), 2000);
                    }).catch(() => {});
                  };

                  const handleLinkedIn = () => {
                    if (isMobile) {
                      navigator.share({
                        title: article.title[lang],
                        text: article.excerpt[lang],
                        url: shareUrl,
                      }).catch(() => {
                        window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`, "_blank", "noopener,noreferrer");
                      });
                    } else {
                      window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`, "_blank", "noopener,noreferrer");
                    }
                  };

                  const handleWhatsApp = () => {
                    const waText = `${article.title[lang]}\n${shareUrl}`;
                    if (isMobile) {
                      window.open(`https://api.whatsapp.com/send?text=${encodeURIComponent(waText)}`, "_blank", "noopener,noreferrer");
                    } else {
                      window.open(`https://web.whatsapp.com/send?text=${encodeURIComponent(waText)}`, "_blank", "noopener,noreferrer");
                    }
                  };

                  return (
                    <>
                      <button onClick={handleLinkedIn} className={btnClass} title="LinkedIn">
                        <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="currentColor"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
                        LinkedIn
                      </button>
                      <button onClick={handleWhatsApp} className={btnClass} title="WhatsApp">
                        <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                        WhatsApp
                      </button>
                      <button onClick={handleCopyLink} className={btnClass} title={t({ pt: "Copiar link", es: "Copiar enlace", en: "Copy link" }, lang)}>
                        {linkCopied ? <Check className="h-3.5 w-3.5 text-green-500" /> : <Copy className="h-3.5 w-3.5" />}
                        {linkCopied
                          ? t({ pt: "Copiado!", es: "¡Copiado!", en: "Copied!" }, lang)
                          : t({ pt: "Copiar link", es: "Copiar enlace", en: "Copy link" }, lang)}
                      </button>
                    </>
                  );
                })()}
              </div>
            </div>

            {/* Article body */}
            <article>
              <div
                className={`prose prose-lg max-w-none ${
                  isDark
                    ? "prose-invert prose-headings:text-[hsl(210,40%,98%)] prose-p:text-[hsl(215,20%,75%)] prose-strong:text-[hsl(210,40%,98%)] prose-code:text-[hsl(197,100%,47%)] prose-pre:bg-[hsl(222,47%,8%)] prose-pre:border prose-pre:border-[hsl(222,30%,18%)] prose-blockquote:border-[hsl(197,100%,47%)]/40 prose-blockquote:text-[hsl(215,20%,65%)] prose-a:text-[hsl(197,100%,47%)] prose-li:text-[hsl(215,20%,75%)]"
                    : "prose-headings:text-gray-900 prose-p:text-gray-700 prose-strong:text-gray-900 prose-code:text-blue-600 prose-pre:bg-gray-50 prose-pre:border prose-pre:border-gray-200 prose-blockquote:border-blue-400 prose-blockquote:text-gray-600 prose-a:text-blue-600 prose-li:text-gray-700"
                }`}
              >
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={{
                    h1: () => null,
                    h2: ({ children, ...props }) => {
                      const text = String(children);
                      const id = slugify(text);
                      return <h2 id={id} className="scroll-mt-20" {...props}>{children}</h2>;
                    },
                    h3: ({ children, ...props }) => {
                      const text = String(children);
                      const id = slugify(text);
                      return <h3 id={id} className="scroll-mt-20" {...props}>{children}</h3>;
                    },
                    pre: (preProps: any) => <CodeBlock {...preProps} isDark={isDark} />,
                  }}
                >
                  {strippedMarkdown}
                </ReactMarkdown>
              </div>
            </article>
          </div>
        </div>
      </div>

      {/* Footer CTA */}
      <div
        className={`border-t py-12 ${
          isDark ? "border-[hsl(222,30%,18%)]" : "border-gray-200"
        }`}
      >
        <div className="container mx-auto px-4 max-w-3xl text-center">
          <p className={`text-sm mb-4 ${isDark ? "text-[hsl(215,20%,65%)]" : "text-muted-foreground"}`}>
            {t(ins.ctaText, lang)}
          </p>
          <Link to={`${localizedPath("home", lang)}#contact`}>
            <Button
              className="bg-[hsl(197,100%,47%)] hover:bg-[hsl(197,100%,40%)] text-[hsl(222,47%,5%)] font-medium"
            >
              {t(ins.ctaButton, lang)}
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default InsightArticle;
